import { useTranslation } from 'react-i18next';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';
import { Reveal } from '../../components/landing/Reveal';

interface Lib {
  name: string;
  url: string;
  license: string;
  purpose: string;
}

const LIBS: Lib[] = [
  { name: 'NestJS', url: 'https://nestjs.com', license: 'MIT', purpose: 'Backend application framework' },
  { name: 'React', url: 'https://react.dev', license: 'MIT', purpose: 'Frontend UI library' },
  { name: 'Vite', url: 'https://vitejs.dev', license: 'MIT', purpose: 'Frontend build tool + dev server' },
  { name: 'TypeScript', url: 'https://www.typescriptlang.org', license: 'Apache-2.0', purpose: 'Language for backend + frontend' },
  { name: 'PostgreSQL', url: 'https://www.postgresql.org', license: 'PostgreSQL', purpose: 'Primary database' },
  { name: 'Redis', url: 'https://redis.io', license: 'BSD-3-Clause (community)', purpose: 'Cache + queue' },
  { name: 'BullMQ', url: 'https://docs.bullmq.io', license: 'MIT', purpose: 'Job queue on Redis' },
  { name: 'Tailwind CSS', url: 'https://tailwindcss.com', license: 'MIT', purpose: 'Utility-first CSS' },
  { name: 'Radix UI', url: 'https://www.radix-ui.com', license: 'MIT', purpose: 'Headless accessible primitives' },
  { name: 'TanStack Query', url: 'https://tanstack.com/query', license: 'MIT', purpose: 'Frontend data fetching' },
  { name: 'Zustand', url: 'https://zustand-demo.pmnd.rs', license: 'MIT', purpose: 'Frontend state for app shell' },
  { name: 'Framer Motion', url: 'https://www.framer.com/motion/', license: 'MIT', purpose: 'Animation primitives' },
  { name: 'Lucide', url: 'https://lucide.dev', license: 'ISC', purpose: 'Icon set' },
  { name: 'i18next', url: 'https://www.i18next.com', license: 'MIT', purpose: 'i18n framework' },
  { name: 'Noto Sans / Noto Serif (JP, KR, SC, Arabic, Devanagari, Bengali)', url: 'https://fonts.google.com/noto', license: 'SIL Open Font License 1.1', purpose: 'Multilingual fonts' },
  { name: 'Inter', url: 'https://rsms.me/inter/', license: 'SIL Open Font License 1.1', purpose: 'Primary UI font' },
  { name: 'JetBrains Mono', url: 'https://www.jetbrains.com/lp/mono/', license: 'SIL Open Font License 1.1', purpose: 'Tabular figures + IDs' },
  { name: 'bcrypt', url: 'https://github.com/kelektiv/node.bcrypt.js', license: 'MIT', purpose: 'Password hashing' },
  { name: 'passport-google-oauth20 / passport-apple', url: 'https://www.passportjs.org', license: 'MIT', purpose: 'OAuth strategies' },
  { name: 'jsonwebtoken', url: 'https://github.com/auth0/node-jsonwebtoken', license: 'MIT', purpose: 'JWT signing + decoding (Apple ID token)' },
  { name: 'iconv-lite', url: 'https://github.com/ashtuchkin/iconv-lite', license: 'MIT', purpose: 'Shift_JIS encoding for Zengin file output and Mizuho / MUFG CSV ingestion' },
  { name: 'pdf-lib', url: 'https://pdf-lib.js.org', license: 'MIT', purpose: 'PDF generation for invoices + close packets' },
  { name: 'Apache ECharts', url: 'https://echarts.apache.org', license: 'Apache-2.0', purpose: 'Chart library' },
  { name: 'AI gateway HTTP client', url: 'https://github.com/openai/openai-node', license: 'Apache-2.0', purpose: 'AI inference SDK (Apache-2.0 attribution preserved)' },
  { name: 'Stripe SDK', url: 'https://github.com/stripe/stripe-node', license: 'MIT', purpose: 'Issuing + Connect + Checkout' },
  { name: 'xml2js', url: 'https://github.com/Leonidas-from-XIV/node-xml2js', license: 'MIT', purpose: 'Peppol UBL XML builder + parser' },
  { name: 'pg', url: 'https://node-postgres.com', license: 'MIT', purpose: 'PostgreSQL driver' },
];

export function OssCreditsPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-16 lg:py-20">
        <Reveal as="div">
          <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
            <span className="sparkle-dot" aria-hidden />
            {t('oss.eyebrow')}
          </span>
          <h1 className="display text-4xl lg:text-5xl font-semibold tracking-tight mt-5">
            {t('oss.title')}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            {t('oss.subtitle')}
          </p>
        </Reveal>

        <Reveal as="div" delay={0.06} className="mt-12 overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-left">
                <th className="px-4 py-3 font-medium text-muted-foreground">{t('oss.cols.name')}</th>
                <th className="px-4 py-3 font-medium text-muted-foreground">{t('oss.cols.purpose')}</th>
                <th className="px-4 py-3 font-medium text-muted-foreground">{t('oss.cols.license')}</th>
              </tr>
            </thead>
            <tbody>
              {LIBS.map((l) => (
                <tr key={l.name} className="border-t border-border align-top">
                  <td className="px-4 py-3">
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-cinnabar-600 dark:text-cinnabar-300 hover:underline"
                    >
                      {l.name}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-foreground/80">{l.purpose}</td>
                  <td className="px-4 py-3 num text-xs text-muted-foreground">{l.license}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>

        <p className="mt-8 text-xs text-muted-foreground leading-relaxed max-w-2xl">{t('oss.footnote')}</p>
      </main>
      <SiteFooter />
    </div>
  );
}
